import { HttpClient } from "@angular/common/http";
import { ComponentRef, EventEmitter, Injectable } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
import { lastValueFrom, Observable } from "rxjs";
import { finalize } from "rxjs/operators";
import { environment } from "src/environments/environment";

import ApiVersionConstants from "../constants/api-version-constants";
import LocalizationConstants from "../constants/localization-constants";
import {
  Language,
  LocalizationInStorage,
  LocalizationListWitDefaultLanguageDto,
} from "../models/Language";
import { Translation } from "../models/Translation";
import { GlobalOverlayService } from "./global-overlay.service";
import { StorageService } from "./storage.service";

@Injectable({
  providedIn: "root",
})
export class LocalizationService {
  private baseUrl_V2 = `${environment.apiConfig.uri}/${ApiVersionConstants.V_2}/localization`;
  private localizationList: Record<string, string>;
  private defaultLangLocalizationList: Record<string, string>;

  componentRef: ComponentRef<unknown>;

  languageList: Language[] = [];
  currentLanguage: Language;
  languageChange: EventEmitter<Language> = new EventEmitter<Language>();

  constructor(
    private http: HttpClient,
    private translate: TranslateService,
    private globalOverlayService: GlobalOverlayService,
    private storageService: StorageService,
  ) {
    this.ensureLanguageList();
    this.ensureCurrentLanguage();
  }

  private setCurrentLanguage(language: Language) {
    this.currentLanguage = language;
    this.storageService.setLanguage(language);
  }

  private ensureLanguageList(): void {
    const languages = this.storageService.getLanguageList();
    if (languages.length > 0) {
      this.languageList = languages;
      return;
    }

    this.globalOverlayService.show();
    this.getAvailableLanguages()
      .pipe(finalize(() => this.globalOverlayService.close()))
      .subscribe((languages) => {
        this.storageService.setLanguageList(languages);
        this.languageList = languages;
        this.ensureCurrentLanguage();
      });
  }

  private ensureCurrentLanguage(): void {
    if (this.currentLanguage) {
      return;
    }

    // if no languages, don't set the language, first we need to retrieve it.
    if (this.languageList.length === 0) {
      return;
    }

    const currentLanguageCode =
      this.storageService.getLanguage()?.code ?? LocalizationConstants.DEFAULT_LANGUAGE_CODE;

    const language = this.languageList.find((x) => x.code.toUpperCase() === currentLanguageCode);

    this.setCurrentLanguage(language);
    this.setLocalization(language.code);
  }

  public isDefaultLanguage = (language: string) =>
    language === LocalizationConstants.DEFAULT_LANGUAGE_CODE;

  public async setLocalization(languageCode: string): Promise<void> {
    this.currentLanguage = this.languageList.find((a) => a.code === languageCode);

    this.localizationList = this.getLocalizationFromLocalStorage(this.currentLanguage.code);
    this.defaultLangLocalizationList = this.getLocalizationFromLocalStorage(
      LocalizationConstants.DEFAULT_LANGUAGE_CODE,
    );

    if (!this.localizationList || !this.defaultLangLocalizationList) {
      await this.getLocalizationWithDefaultLanguageAsync(languageCode);
    }

    this.configureTranslationService();
  }

  private configureTranslationService(): void {
    // set the translation for the given language
    this.translate.setTranslation(this.currentLanguage.code, this.localizationList);

    // set the default language translation (so it can be used as fall-back) in case
    // some localization values are not setup for the given list.
    this.translate.setTranslation(
      LocalizationConstants.DEFAULT_LANGUAGE_CODE,
      this.defaultLangLocalizationList,
    );

    this.storageService.setLanguage(this.currentLanguage);

    // specify the default language in translationService
    this.translate.setDefaultLang(LocalizationConstants.DEFAULT_LANGUAGE_CODE);

    this.translate.use(this.currentLanguage.code);
  }

  private async getLocalizationWithDefaultLanguageAsync(languageCode: string): Promise<void> {
    this.globalOverlayService.show();

    const localizationWithDefaultLanguage = await lastValueFrom(this.getLocalization(languageCode));

    this.localizationList = localizationWithDefaultLanguage.language;
    this.defaultLangLocalizationList = localizationWithDefaultLanguage.defaultLanguage;

    // store values in local storage for the given language
    this.storageService.setLocalization(
      this.currentLanguage.code,
      new LocalizationInStorage(this.currentLanguage.modifyDate, this.localizationList),
    );

    // store values in local storage for the default language
    const defaultLanguage = this.languageList.find(
      (a) => a.code === LocalizationConstants.DEFAULT_LANGUAGE_CODE,
    );

    this.storageService.setLocalization(
      LocalizationConstants.DEFAULT_LANGUAGE_CODE,
      new LocalizationInStorage(defaultLanguage.modifyDate, this.defaultLangLocalizationList),
    );

    this.globalOverlayService.close();
  }

  private getLocalizationFromLocalStorage(languageCode: string): Record<string, string> | null {
    const language = this.languageList.find((a) => a.code === languageCode);
    const currentLanguageModifiedDate = language?.modifyDate;

    if (!currentLanguageModifiedDate) {
      return null;
    }

    const localization = this.storageService.getLocalization(languageCode);
    return currentLanguageModifiedDate !== localization?.modified ? null : localization.value;
  }

  private getLocalization(language: string): Observable<LocalizationListWitDefaultLanguageDto> {
    return this.http.get<LocalizationListWitDefaultLanguageDto>(`${this.baseUrl_V2}/${language}`);
  }

  public getAvailableLanguages(): Observable<Language[]> {
    return this.http.get<Language[]>(this.baseUrl_V2);
  }

  public getAllLanguages(): Observable<Language[]> {
    return this.http.get<Language[]>(`${this.baseUrl_V2}/all`);
  }

  public addLanguage(language) {
    return this.http.post(this.baseUrl_V2, language);
  }

  public updateLanguage(language) {
    return this.http.put(this.baseUrl_V2, language);
  }

  public getLocalizationListForLanguage(language: string): Observable<Translation[]> {
    return this.http.get<Translation[]>(`${this.baseUrl_V2}/list/${language}`);
  }

  public deleteLocalization(languageCode: string, key: string) {
    return this.http.delete(`${this.baseUrl_V2}/${languageCode}/${key}`);
  }

  public updateLocalization(language: string, item: Record<string, string>): Observable<void> {
    return this.http.put<void>(`${this.baseUrl_V2}/${language}`, item);
  }

  public addLocalization(language: string, item) {
    return this.http.post(`${this.baseUrl_V2}/${language}`, item);
  }

  public bulkAddLocalization(language: string, localizations: unknown[]) {
    return this.http.post(`${this.baseUrl_V2}/${language}/bulk`, localizations);
  }
}
